/**
 * aiCatalogImport.ts — desktop client for the backend AI catalog extractor.
 *
 * Flow: user uploads a photo/scan of a supplier bill or PO → we POST it to
 * `POST /api/ai/parse-invoice-catalog` (Gemini/Vertex on the server) → get back
 * structured line items → dedupe against the existing catalog → the screen lets
 * the user review/edit → commit into Inventory.
 *
 * The backend accepts raster images only (jpeg/png/webp, ≤5 MB) and the call
 * consumes the merchant's AI quota. PDFs must be rasterised to an image first
 * (not handled here).
 */

import { Q } from '@nozbe/watermelondb';
import { API_BASE_URL } from '@/config';
import { tokenStorage } from '@/services/httpClient';
import { database } from '@/db';
import { Product, Category } from '@/db/models/Inventory';
import { createProduct, createCategory, adjustStock, updateProduct } from './InventoryCrudService';

// ── Backend response shape (mirrors gemini.service.ts ParsedCatalogResult) ──
export interface ParsedCatalogLineItem {
  name: string;
  barcode?: string;
  quantity?: number;
  unit?: string;
  mrp?: number;
  purchase_price?: number;
  selling_price?: number;
  hsn_code?: string;
  gst_rate?: number;
  category_suggestion?: string;
}

export interface ParsedCatalogResult {
  supplier_name?: string;
  supplier_gstin?: string;
  invoice_number?: string;
  invoice_date?: string;
  line_items: ParsedCatalogLineItem[];
  confidence: 'high' | 'medium' | 'low';
  low_confidence_fields: string[];
  parsing_notes?: string;
}

/**
 * Upload a bill image and get back extracted catalog line items.
 * Throws Error with a user-facing message on failure.
 */
export async function parseInvoiceForCatalog(file: Blob): Promise<ParsedCatalogResult> {
  const token = await tokenStorage.getAccessToken();
  if (!token) throw new Error('You are signed out. Sign in again to use AI import.');

  const form = new FormData();
  form.append('file', file, (file as File).name || 'bill.jpg');

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/ai/parse-invoice-catalog`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
  } catch {
    throw new Error('Could not reach the server. Check your internet connection and try again.');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(friendlyError(res.status, text));
  }

  const result = (await res.json()) as ParsedCatalogResult;
  if (!result || !Array.isArray(result.line_items)) {
    throw new Error('The server returned an unexpected response. Please try a clearer photo.');
  }
  return result;
}

function friendlyError(status: number, body: string): string {
  const msg = extractMessage(body);
  switch (status) {
    case 400: return msg || 'That file could not be read. Upload a clear JPG/PNG photo of the bill.';
    case 401: return 'Your session expired. Sign in again and retry.';
    case 402:
    case 429: return msg || 'Your AI scan limit for this plan is reached. Upgrade or try again later.';
    case 413: return 'That image is too large (max 5 MB). Retake at a lower resolution.';
    default:  return status >= 500 ? 'AI parsing failed on the server. Try again in a moment.' : (msg || `Request failed (${status}).`);
  }
}

function extractMessage(body: string): string {
  try {
    const j = JSON.parse(body);
    return typeof j?.message === 'string' ? j.message : Array.isArray(j?.message) ? j.message.join(', ') : '';
  } catch {
    return '';
  }
}

// ── Dedup against the existing catalog ───────────────────────────────────────
export type MatchStatus =
  | { type: 'new' }
  | { type: 'existing'; productId: string; productName: string };

/**
 * For each parsed item, decide whether it already exists in the store's catalog.
 * Matches by barcode (exact) first, then by case-insensitive trimmed name.
 */
export async function dedupeAgainstCatalog(
  items: ParsedCatalogLineItem[],
  storeId: string,
): Promise<MatchStatus[]> {
  const existing = await database
    .get<Product>('products')
    .query(Q.where('store_id', storeId), Q.where('is_deleted', false))
    .fetch();

  const byBarcode = new Map<string, Product>();
  const byName = new Map<string, Product>();
  for (const p of existing) {
    if (p.barcode) byBarcode.set(p.barcode.trim(), p);
    if (p.name) byName.set(p.name.trim().toLowerCase(), p);
  }

  return items.map((item) => {
    const bc = item.barcode?.trim();
    const hit = (bc && byBarcode.get(bc)) || byName.get(item.name.trim().toLowerCase());
    return hit
      ? { type: 'existing' as const, productId: hit.id, productName: hit.name }
      : { type: 'new' as const };
  });
}

// ── Commit ───────────────────────────────────────────────────────────────────
export interface ImportRow {
  include: boolean;
  name: string;
  categoryName: string | null;
  hsnCode: string | null;
  gstRate: number;
  quantity: number;
  purchasePrice: number;
  sellingPrice: number;
  mrp: number;
  barcode: string | null;
  unit: string;
  match: MatchStatus;
}

export interface CommitResult {
  created: number;
  updated: number;
  skipped: number;
  errors: { name: string; message: string }[];
}

/**
 * Import the selected rows into Inventory.
 *  • new  → creates a Product (stock = quantity)
 *  • existing → adds `quantity` to stock (audited) and refreshes purchase price
 * Missing categories are created on the fly so no category is silently dropped.
 */
export async function commitCatalogImport(
  rows: ImportRow[],
  ctx: { storeId: string; merchantId: string; supplierId?: string | null; reference?: string | null },
): Promise<CommitResult> {
  const result: CommitResult = { created: 0, updated: 0, skipped: 0, errors: [] };

  // Resolve / create categories once.
  const cats = await database
    .get<Category>('categories')
    .query(Q.where('merchant_id', ctx.merchantId), Q.where('is_deleted', false))
    .fetch();
  const catByName = new Map<string, string>(); // lower name → id
  for (const c of cats) catByName.set(c.name.trim().toLowerCase(), c.id);

  async function resolveCategoryId(name: string | null): Promise<string | null> {
    const clean = name?.trim();
    if (!clean) return null;
    const key = clean.toLowerCase();
    const existing = catByName.get(key);
    if (existing) return existing;
    try {
      const created = await createCategory(ctx.merchantId, clean);
      catByName.set(key, created.id);
      return created.id;
    } catch {
      return null; // fall back to uncategorized rather than fail the row
    }
  }

  for (const row of rows) {
    if (!row.include) { result.skipped++; continue; }
    try {
      if (row.match.type === 'existing') {
        const product = await database.get<Product>('products').find(row.match.productId);
        if (row.quantity > 0) {
          await adjustStock(product, row.quantity, `AI import${ctx.reference ? ` · ${ctx.reference}` : ''}`, {});
        }
        if (row.purchasePrice > 0 && row.purchasePrice !== product.purchasePrice) {
          await updateProduct(product, { purchasePrice: row.purchasePrice });
        }
        result.updated++;
      } else {
        const categoryId = await resolveCategoryId(row.categoryName);
        const sellingPrice = row.sellingPrice > 0 ? row.sellingPrice : (row.mrp > 0 ? row.mrp : row.purchasePrice);
        await createProduct({
          storeId: ctx.storeId,
          supplierId: ctx.supplierId ?? null,
          categoryId,
          name: row.name.trim(),
          barcode: row.barcode?.trim() || null,
          hsnCode: row.hsnCode?.trim() || null,
          mrp: row.mrp,
          sellingPrice,
          purchasePrice: row.purchasePrice,
          currentStock: Math.max(0, row.quantity),
          unit: row.unit || 'PCS',
          gstRate: row.gstRate,
        });
        result.created++;
      }
    } catch (err) {
      result.errors.push({ name: row.name, message: err instanceof Error ? err.message : 'Failed to import' });
    }
  }

  return result;
}
